/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Component } from '@appCanvasCommon/component-system/Component';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { Circle, Rectangle, Polygon, Sprite } from 'pixi.js-legacy';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';

export class TicketAccessibility extends Component implements ComponentInterface {
	private _canvasAngularLand: CanvasAngularLand;
	private _ticketContainer: TicketContainer;

	private accessibleChild: Sprite;

	constructor() {
		super();
	}
	/**
	 * The setup method is called once all properties on a component have been decorated by the ComponentManager.
	 *
	 * From this point on, you can safely use this.getParent() and this.getManager().
	 *
	 * Arguments passed into setup are passed in via the ComponentManager.add(name, component, ...args)
	 */
	public override setup(canvasAngularLand: CanvasAngularLand, dataContainer: TicketContainer): void {
		this.canvasAngularLand = canvasAngularLand;
		this._canvasAngularLand = canvasAngularLand;
		this._ticketContainer = dataContainer;

		const parent = this.getParent();

		const data = dataContainer.getData();

		// Since the parent can have a hitArea of type 'polygon' or 'circle' and AccessibilityManager only works with
		// rectangles, we'll create a new sprite with the converted hit area.
		this.accessibleChild = new Sprite();
		this.accessibleChild.eventMode = EventMode.Static;
		this.accessibleChild.accessible = true;
		this.accessibleChild.accessibleTitle = JSON.stringify({
			id: data.$id,
			name: data.view.activityName,
			type: data.view.type,
		});

		// Set hit area after a while. For some reason, data is not present yet.
		setTimeout(() => {
			this.accessibleChild.hitArea = this.copyHitAreaAsRectangle(parent.hitArea);
		}, 1);

		// Add accessible child
		parent.addChild(this.accessibleChild);
	}

	private copyHitAreaAsRectangle(hitArea: any): Rectangle {
		let result: Rectangle;
		if (hitArea instanceof Rectangle) {
			result = new Rectangle(hitArea.x, hitArea.y, hitArea.width, hitArea.height);
		} else if (hitArea instanceof Circle) {
			result = new Rectangle(
				hitArea.x - hitArea.radius,
				hitArea.y - hitArea.radius,
				hitArea.radius * 2,
				hitArea.radius * 2
			);
		} else if (hitArea instanceof Polygon) {
			const poly = <Polygon>hitArea;
			let minX = 1;
			let minY = 1;
			let maxX = -1;
			let maxY = -1;
			for (let i = 0; i < poly.points.length; i += 2) {
				if (minX > poly.points[i]) {
					minX = poly.points[i];
				}
				if (minY > poly.points[i + 1]) {
					minY = poly.points[i + 1];
				}
				if (maxX < poly.points[i]) {
					maxX = poly.points[i];
				}
				if (maxY < poly.points[i + 1]) {
					maxY = poly.points[i + 1];
				}
			}
			result = new Rectangle(minX, minY, maxX - minX, maxY - minY);
		}
		return result;
	}
}
